import { PalmettoIcon } from '@palmetto/dsm-components';
import { useCallback } from 'react';

export interface PaginationProps {
  /** Zero-indexed current page. */
  page: number;
  /** Total number of pages (>= 1). */
  totalPages: number;
  /** Called with the new zero-indexed page. */
  onPageChange: (page: number) => void;
}

const buttonBase =
  'flex h-[44px] w-[44px] items-center justify-center rounded-12 border-2 border-solid border-transparent bg-transparent text-btn-bg-primary-idle';

export const Pagination = ({ page, totalPages, onPageChange }: PaginationProps) => {
  const isFirstPage = page <= 0;
  const isLastPage = page >= totalPages - 1;

  const handlePrevious = useCallback(() => onPageChange(Math.max(0, page - 1)), [onPageChange, page]);
  const handleNext = useCallback(
    () => onPageChange(Math.min(totalPages - 1, page + 1)),
    [onPageChange, page, totalPages],
  );

  if (totalPages <= 1) return null;

  return (
    <nav aria-label="Pagination" className="flex flex-row items-center justify-center gap-6">
      <button
        type="button"
        aria-label="Previous page"
        onClick={handlePrevious}
        disabled={isFirstPage}
        className={`${buttonBase}${isFirstPage ? ' opacity-40' : ''}`}
      >
        <PalmettoIcon name="chevronLeft" size="md" />
      </button>

      <span className="ui-400 whitespace-nowrap text-text-secondary">
        Page {page + 1} of {totalPages}
      </span>

      <button
        type="button"
        aria-label="Next page"
        onClick={handleNext}
        disabled={isLastPage}
        className={`${buttonBase}${isLastPage ? ' opacity-40' : ''}`}
      >
        <PalmettoIcon name="chevronRight" size="md" />
      </button>
    </nav>
  );
};
